// Offline, text-searchable A4 usage report PDF. No third-party calls or raw events.
// Fixed, non-overlapping panel coordinates replace the old vertically packed table.
const W = 595, H = 842;
const COLOR = {
  navy: '0.090 0.212 0.357',
  text: '0.141 0.220 0.318',
  secondary: '0.345 0.424 0.522',
  surface: '0.949 0.969 0.988',
  line: '0.843 0.894 0.941',
  bar: '0.658 0.811 0.914',
};
const LANGUAGE_NAMES = {
  ar: 'Arabic', bn: 'Bengali', en: 'English', es: 'Spanish', fa: 'Farsi',
  fil: 'Filipino', fr: 'French', gu: 'Gujarati', it: 'Italian',
  pa: 'Punjabi', pl: 'Polish', pt: 'Portuguese', ro: 'Romanian',
  so: 'Somali', tr: 'Turkish', ur: 'Urdu', zh: 'Chinese',
};

function clean(value) {
  return String(value ?? '').normalize('NFKD')
    .replace(/[\u2012-\u2015]/g, '-')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/[^\x20-\x7E]/g, '?');
}
function escapePdf(value) {
  return clean(value).replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
}
function monthLabel(month) {
  const parts = month.split('-').map(Number);
  return new Date(Date.UTC(parts[0], parts[1] - 1, 1)).toLocaleDateString('en-GB', {
    month: 'long', year: 'numeric', timeZone: 'UTC',
  });
}
function displayed(value) {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.round(n).toLocaleString('en-GB') : '0';
}
function responseLabel(measure) {
  const n = Number(measure?.responses || 0);
  return n > 0 && measure?.positiveRate != null
    ? measure.positiveRate + '% (' + displayed(n) + ' responses)'
    : 'N/A';
}
function createPainter() {
  let commands = [];
  const add = value => commands.push(value);
  function rect(x, y, w, h, color) {
    add(color + ' rg ' + x + ' ' + y + ' ' + w + ' ' + h + ' re f');
  }
  function rounded(x, y, w, h, r, color) {
    const a = +(r * 0.55228475).toFixed(3), x2 = x + w, y2 = y + h;
    add(color + ' rg ' +
      (x + r) + ' ' + y + ' m ' + (x2 - r) + ' ' + y + ' l ' +
      (x2 - r + a) + ' ' + y + ' ' + x2 + ' ' + (y + r - a) + ' ' + x2 + ' ' + (y + r) + ' c ' +
      x2 + ' ' + (y2 - r) + ' l ' + x2 + ' ' + (y2 - r + a) + ' ' + (x2 - r + a) + ' ' + y2 + ' ' + (x2 - r) + ' ' + y2 + ' c ' +
      (x + r) + ' ' + y2 + ' l ' + (x + r - a) + ' ' + y2 + ' ' + x + ' ' + (y2 - r + a) + ' ' + x + ' ' + (y2 - r) + ' c ' +
      x + ' ' + (y + r) + ' l ' + x + ' ' + (y + r - a) + ' ' + (x + r - a) + ' ' + y + ' ' + (x + r) + ' ' + y + ' c h f');
  }
  function text(value, x, y, size = 10, bold = false, color = COLOR.text) {
    add('BT ' + color + ' rg /' + (bold ? 'F2' : 'F1') + ' ' + size +
      ' Tf 1 0 0 1 ' + x + ' ' + y + ' Tm (' + escapePdf(value) + ') Tj ET');
  }
  function line(x1, y1, x2, y2) {
    add(COLOR.line + ' RG 0.65 w ' + x1 + ' ' + y1 + ' m ' + x2 + ' ' + y2 + ' l S');
  }
  function reset() { commands = []; }
  function finish() { return commands.join('\n') + '\n'; }
  return { rect, rounded, text, line, reset, finish };
}
function pageHeader(p, report, isContinuation = false) {
  const title = 'KNOW YOUR CARE  /  USAGE REPORT';
  const rawTrustName = clean(report.trustName || report.trustId);
  const trustName = rawTrustName.length > 66 ? rawTrustName.slice(0, 63) + '...' : rawTrustName;
  p.rect(0, 831, W, 11, COLOR.navy);
  p.text(title, 45, 789, 18, true, COLOR.navy);
  p.text(trustName, 45, 762, 12, true);
  const month = monthLabel(report.month);
  const partial = report.period?.complete ? 'Complete month (UTC)' : 'Partial month (UTC)';
  const when = String(report.generatedAt || '').slice(0, 10);
  p.text(month + '  |  ' + partial + '  |  Generated ' + when + (isContinuation ? '  |  Detail' : ''),
    45, 741, 9.5, false, COLOR.secondary);
  p.line(45, 723, 550, 723);
}
function pageFooter(p, page, total) {
  p.line(45, 61, 550, 61);
  p.text('Know Your Care  |  Anonymous usage report - not patient counts', 45, 44, 8, false, COLOR.secondary);
  p.text('Page ' + page + ' / ' + total, 510, 44, 8, false, COLOR.secondary);
}
function panelTitle(p, title, x, y) {
  p.text(title, x + 16, y, 10, true, COLOR.navy);
}
function reportNotes(report) {
  const m = report.metrics || {};
  const historicalStarts = report.month <= '2026-10';
  return [
    'Counts are app events, not unique people or matched sessions.',
    'Generic NHS starts: ' + displayed(m.genericJourneyStarts) + '; unverified starts: ' + displayed(m.unverifiedJourneyStarts) + '.',
    historicalStarts
      ? 'Journey starts were introduced on 8 October 2026; earlier starts are missing.'
      : 'Journey activity counts are not a matched-session conversion funnel.',
    'Reaching Journey end differs from submitting feedback; surveys are separate.',
  ];
}
function mainPage(p, report) {
  pageHeader(p, report);
  const m = report.metrics || {};
  const summary = [
    ['TRACKED EVENTS', m.trackedEvents],
    ['JOURNEY VIEWS', m.journeyViews],
    ['ROLE GUIDE VIEWS', m.roleViews],
    ['QR ENTRIES', m.qrEntries],
  ];
  summary.forEach(([label, count], i) => {
    const x = 45 + i * 130;
    p.rounded(x, 628, 115, 80, 9, COLOR.surface);
    p.text(displayed(count), x + 13, 661, 24, true, COLOR.navy);
    p.text(label, x + 13, 642, 8, true, COLOR.secondary);
  });
  p.rounded(45, 420, 245, 189, 9, COLOR.surface);
  p.rounded(305, 420, 245, 189, 9, COLOR.surface);
  panelTitle(p, 'JOURNEY ACTIVITY', 45, 581);
  panelTitle(p, 'RESPONSE MEASURES', 305, 581);
  for (const [y, label, count] of [
    [551, 'Journey starts', m.journeyStarts],
    [520, 'Reached end', m.journeyEnds],
    [489, 'Preparedness answers', m.preparedness?.responses],
  ]) {
    p.text(label, 61, y, 10);
    p.text(displayed(count), 261, y, 12, true, COLOR.navy);
  }
  p.text(report.month <= '2026-10' ? 'Starts tracked from 8 October 2026.' : 'Independent event totals, not a funnel.',
    61, 448, 8.5, false, COLOR.secondary);
  p.text('Role guide helpful', 320, 552, 10);
  p.text(responseLabel(m.helpfulness), 320, 528, 13, true, COLOR.navy);
  p.text('Reported preparedness', 320, 498, 10);
  p.text(responseLabel(m.preparedness), 320, 474, 13, true, COLOR.navy);
  p.text('Percentages are based on received answers.', 320, 444, 8.2, false, COLOR.secondary);

  p.rounded(45, 247, 245, 159, 9, COLOR.surface);
  p.rounded(305, 247, 245, 159, 9, COLOR.surface);
  panelTitle(p, 'LANGUAGE ACTIVITY', 45, 377);
  panelTitle(p, 'PUBLISHED TRUST JOURNEYS', 305, 377);
  const languages = Array.isArray(m.languages) ? m.languages : [];
  const maxLanguage = Math.max(1, ...languages.map(x => Number(x.events || 0)));
  languages.slice(0, 4).forEach((item, index) => {
    const y = 349 - 27 * index;
    const id = String(item.id || '').toLowerCase().split('-')[0];
    p.text((LANGUAGE_NAMES[id] || clean(item.id)).slice(0, 15), 61, y, 9.5);
    const ratio = Math.max(0, Number(item.events || 0)) / maxLanguage;
    if (ratio > 0) p.rounded(132, y - 1, Math.max(4, Math.round(100 * ratio)), 8, 4, COLOR.bar);
    p.text(displayed(item.events), 263, y, 10, true, COLOR.navy);
  });
  if (!languages.length) {
    p.text('No language-attributed events', 61, 330, 9, false, COLOR.secondary);
    p.text('were recorded in this month.', 61, 315, 9, false, COLOR.secondary);
  }
  const journeys = Array.isArray(m.journeys) ? m.journeys : [];
  journeys.slice(0, 4).forEach((item, index) => {
    const y = 346 - 27 * index;
    p.text(clean(item.id).slice(0, 27), 320, y, 9.5);
    p.text(displayed(item.events), 521, y, 10, true, COLOR.navy);
  });
  if (!journeys.length) {
    p.text('No verified Trust-published', 320, 331, 9, false, COLOR.secondary);
    p.text('Journey starts this month.', 320, 316, 9, false, COLOR.secondary);
  }
  p.rounded(45, 86, 505, 145, 9, COLOR.surface);
  p.text('HOW TO INTERPRET', 61, 204, 10, true, COLOR.navy);
  reportNotes(report).forEach((note, i) => {
    p.text('-  ' + clean(note).slice(0, 88), 61, 179 - 23 * i, 9, false, COLOR.secondary);
  });
}
function appendixPage(p, report) {
  pageHeader(p, report, true);
  p.text('FULL ACTIVITY BREAKDOWN', 45, 686, 13, true, COLOR.navy);
  p.text('Breakdown shown on this page when more than four languages or Journeys are present.',
    45, 663, 9, false, COLOR.secondary);
  const m = report.metrics || {};
  const all = [
    ['Language', m.languages || []],
    ['Published Trust Journey starts', m.journeys || []],
  ];
  let y = 626;
  for (const [title, rows] of all) {
    p.rounded(45, y - 31, 505, 40, 7, COLOR.surface);
    p.text(title.toUpperCase(), 58, y - 14, 10, true, COLOR.navy);
    y -= 55;
    for (const item of rows) {
      if (y < 93) break;
      const id = String(item.id || '');
      const normalized = title === 'Language' ? LANGUAGE_NAMES[id.toLowerCase().split('-')[0]] || id : id;
      p.text(clean(normalized).slice(0, 58), 58, y, 9.5);
      p.text(displayed(item.events), 500, y, 10, true, COLOR.navy);
      p.line(58, y - 9, 535, y - 9);
      y -= 25;
    }
    y -= 14;
  }
}
function makeStreams(report) {
  const painter = createPainter();
  const m = report.metrics || {};
  const extra = (m.languages || []).length > 4 || (m.journeys || []).length > 4;
  const total = extra ? 2 : 1;
  painter.reset();
  mainPage(painter, report);
  pageFooter(painter, 1, total);
  const streams = [painter.finish()];
  if (extra) {
    painter.reset();
    appendixPage(painter, report);
    pageFooter(painter, 2, total);
    streams.push(painter.finish());
  }
  return streams;
}
export function createPilotEvidencePdf(report) {
  if (!report?.ok || !report?.trustId || !/^\d{4}-(0[1-9]|1[0-2])$/.test(report.month || '')) {
    throw new Error('A valid monthly usage report response is required');
  }
  const objects = [];
  const add = body => { objects.push(body); return objects.length; };
  add('<< /Type /Catalog /Pages 2 0 R >>');
  add('placeholder');
  const fontRegular = add('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>');
  const fontBold = add('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>');
  const ids = [];
  for (const stream of makeStreams(report)) {
    const content = add('<< /Length ' + stream.length + ' >>\nstream\n' + stream + 'endstream');
    ids.push(add('<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ' + W + ' ' + H +
      '] /Resources << /Font << /F1 ' + fontRegular + ' 0 R /F2 ' + fontBold +
      ' 0 R >> >> /Contents ' + content + ' 0 R >>'));
  }
  objects[1] = '<< /Type /Pages /Kids [' + ids.map(id => id + ' 0 R').join(' ') +
    '] /Count ' + ids.length + ' >>';
  let output = '%PDF-1.4\n';
  const offsets = [0];
  for (let i = 0; i < objects.length; i++) {
    offsets.push(output.length);
    output += (i + 1) + ' 0 obj\n' + objects[i] + '\nendobj\n';
  }
  const startxref = output.length;
  output += 'xref\n0 ' + (objects.length + 1) + '\n0000000000 65535 f \n';
  for (const offset of offsets.slice(1)) {
    output += String(offset).padStart(10, '0') + ' 00000 n \n';
  }
  output += 'trailer\n<< /Size ' + (objects.length + 1) +
    ' /Root 1 0 R >>\nstartxref\n' + startxref + '\n%%EOF\n';
  return new Blob([new TextEncoder().encode(output)], { type: 'application/pdf' });
}
export function downloadPilotEvidencePdf(report) {
  const pdf = createPilotEvidencePdf(report);
  const url = URL.createObjectURL(pdf);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'KYC_Usage_Report_' + report.trustId.replace(/[^a-z0-9_-]/gi, '_') +
    '_' + report.month + '.pdf';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}
